/**
 * Läden: Für jeden Laden lässt sich festlegen, in welcher Reihenfolge die
 * Warengruppen auf dem Weg durch die Gänge kommen (ziehen oder Pfeile). Die
 * Einkaufsliste sortiert sich dann danach. Der Name lässt sich ändern.
 */
import { categoryLabel } from '@cookbook/domain';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../../api/client.ts';
import type { Store } from '../../api/types.ts';
import { useDragList } from '../../lib/useDragReorder.ts';
import { texts } from '../../texts/de.ts';
import { DragHandle } from '../DragHandle.tsx';

/** Eigenschaften der Ladenverwaltung. */
export interface StoresPanelProps {
  readonly stores: readonly Store[];
  readonly onChange: (stores: Store[]) => void;
}

/** Ein Laden: Name, Reihenfolge der Gänge, Löschen. */
function StoreCard({
  store,
  onSave,
  onDelete,
}: {
  readonly store: Store;
  readonly onSave: (changes: { name: string; aisleOrder: string[] }) => void;
  readonly onDelete: () => void;
}) {
  const [name, setName] = useState(store.name);
  const drag = useDragList(store.aisleOrder, (aisleOrder) => onSave({ name: store.name, aisleOrder }));

  // Ändert sich der Name von außen (anderes Gerät), das Feld nachziehen.
  useEffect(() => setName(store.name), [store.name]);

  /** Verschiebt eine Warengruppe um eine Stelle. */
  function move(index: number, direction: -1 | 1) {
    const order = [...store.aisleOrder];
    const target = index + direction;
    const a = order[index];
    const b = order[target];
    if (a === undefined || b === undefined) {
      return;
    }
    order[index] = b;
    order[target] = a;
    onSave({ name: store.name, aisleOrder: order });
  }

  const trimmed = name.trim();
  return (
    <details className="rounded-card border border-border-subtle bg-surface-raised p-3">
      <summary className="cursor-pointer font-semibold">{store.name}</summary>
      <form
        className="mt-3 flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (trimmed !== '' && trimmed !== store.name) {
            onSave({ name: trimmed, aisleOrder: [...store.aisleOrder] });
          }
        }}
      >
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          aria-label={texts.shopping.storeName}
          className="min-w-0 flex-1 rounded-lg border border-border-subtle bg-surface px-3 py-1.5 text-sm"
        />
        <button
          type="submit"
          disabled={trimmed === '' || trimmed === store.name}
          className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm disabled:opacity-50"
        >
          {texts.shopping.renameStore}
        </button>
      </form>
      <p className="mt-3 mb-1 text-xs text-content-muted">{texts.shopping.aisleOrder}</p>
      <ol className="space-y-1">
        {drag.order.map((category, index) => (
          <li
            key={category}
            {...drag.itemProps(category)}
            className={`flex items-center gap-2 rounded-lg bg-surface-sunken px-1 py-0.5 text-sm ${
              drag.dragging === category ? 'relative z-10 shadow-lg ring-2 ring-accent' : ''
            }`}
          >
            <DragHandle label={categoryLabel(category)} {...drag.handleProps(category)} />
            <span className="w-6 text-right text-xs text-content-muted tabular-nums">{index + 1}.</span>
            <span className="flex-1">{categoryLabel(category)}</span>
            <button
              type="button"
              onClick={() => move(index, -1)}
              disabled={index === 0}
              aria-label={`${categoryLabel(category)}: ${texts.shopping.moveUp}`}
              className="rounded px-2 disabled:opacity-30"
            >
              ↑
            </button>
            <button
              type="button"
              onClick={() => move(index, 1)}
              disabled={index === store.aisleOrder.length - 1}
              aria-label={`${categoryLabel(category)}: ${texts.shopping.moveDown}`}
              className="rounded px-2 disabled:opacity-30"
            >
              ↓
            </button>
          </li>
        ))}
      </ol>
      <button
        type="button"
        onClick={onDelete}
        className="mt-3 rounded-lg border border-border-subtle px-3 py-1.5 text-sm text-content-muted"
      >
        {texts.shopping.deleteStore}
      </button>
    </details>
  );
}

/** Verwaltung der Läden. */
export function StoresPanel({ stores, onChange }: StoresPanelProps) {
  const [name, setName] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  /** Führt eine Änderung aus und übernimmt die neue Liste der Läden. */
  async function run(work: () => Promise<{ stores: Store[] }>) {
    setMessage(null);
    try {
      onChange((await work()).stores);
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : texts.common.error);
    }
  }

  /** Speichert Name und Reihenfolge eines Ladens; die Anzeige folgt sofort. */
  function save(store: Store, changes: { name: string; aisleOrder: string[] }) {
    onChange(stores.map((entry) => (entry.id === store.id ? { ...entry, ...changes } : entry)));
    void run(() => api.put(`/api/stores/${store.id}`, changes));
  }

  return (
    <div>
      <form
        className="mb-4 flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim() === '') {
            return;
          }
          void run(() => api.post('/api/stores', { name }));
          setName('');
        }}
      >
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={texts.shopping.storeName}
          aria-label={texts.shopping.storeName}
          className="flex-1 rounded-xl border border-border-subtle bg-surface-raised px-3 py-2.5"
        />
        <button
          type="submit"
          disabled={name.trim() === ''}
          className="rounded-xl bg-accent px-4 font-semibold text-on-accent disabled:opacity-50"
        >
          {texts.shopping.newStore}
        </button>
      </form>
      {message === null ? null : (
        <p className="mb-3 rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">{message}</p>
      )}
      {stores.length === 0 ? (
        <p className="rounded-card border border-border-subtle bg-surface-raised p-6 text-center text-sm text-content-muted">
          {texts.shopping.storesEmpty}
        </p>
      ) : null}
      <div className="space-y-3">
        {stores.map((store) => (
          <StoreCard
            key={store.id}
            store={store}
            onSave={(changes) => save(store, changes)}
            onDelete={() => void run(() => api.delete(`/api/stores/${store.id}`))}
          />
        ))}
      </div>
    </div>
  );
}
